import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Typography
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlined';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axiosInstance from 'src/axios';
import {
  AddressForm,
  AppointmentForm,
  ClinicalNotesForm,
  ConsentForm,
  ContactForm,
  ReferralForm,
  SampleForm,
  TestForm,
  TreatmentForm,
  TissuePathologyForm
} from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/FormsExport';

const CLINICAL_NOTE_TYPES = [
  'Address',
  'Appointment',
  'Clinical Note',
  'Consent',
  'Contact',
  'Referral',
  'Sample',
  'Test',
  'Treatment',
  'Tissue Pathology'
];

export default function BulkClinicalSection({ memberRecords, familyId }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedPids, setSelectedPids] = useState(
    () => new Set(memberRecords.map((m) => m.pid))
  );
  const [staffMembers, setStaffMembers] = useState([]);

  const [formOpen, setFormOpen] = useState(false);
  const [firstMember, setFirstMember] = useState(null);
  const [replaying, setReplaying] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState({
    edit: false,
    status: false
  });
  const [lastCreated, setLastCreated] = useState(null);

  // Refs that stay fresh inside stable callbacks
  const remainingMembersRef = useRef([]); // all selected members except first
  const capturedPayloadRef = useRef(null); // { url, data } from intercepted PUT
  const interceptorIdRef = useRef(null);
  const interceptActiveRef = useRef(false);
  const selectedTypeRef = useRef('');
  const totalRef = useRef(0);

  const staffMemberOptions = useMemo(
    () =>
      staffMembers.map((d) => ({
        label: `${d.firstName} ${d.lastName}`,
        value: d.userId
      })),
    [staffMembers]
  );

  useEffect(() => {
    if (expanded && staffMembers.length === 0) {
      axiosInstance.get('/clinical-notes/staff-members').then((res) => {
        setStaffMembers(
          res.data.map((m) => ({
            firstName: m.firstName,
            lastName: m.lastName,
            userId: m._id
          }))
        );
      });
    }
  }, [expanded]);

  useEffect(() => {
    setSelectedPids(new Set(memberRecords.map((m) => m.pid)));
  }, [memberRecords]);

  const togglePid = (pid) => {
    setSelectedPids((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  // Stable handleClose — on save: deactivates interceptor, replays for remaining members
  const handleClose = useCallback(async (event) => {
    if (event?.exit) {
      // Deactivate the interceptor
      interceptActiveRef.current = false;
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }

      const captured = capturedPayloadRef.current;
      const remaining = remainingMembersRef.current;
      const type = selectedTypeRef.current;
      const total = totalRef.current;

      setFormOpen(false);
      setFirstMember(null);
      capturedPayloadRef.current = null;
      remainingMembersRef.current = [];
      setUnsavedChanges({ edit: false, status: false });

      if (captured && remaining.length > 0) {
        setReplaying(true);
        let saved = 1; // first member already saved by the form
        for (const member of remaining) {
          try {
            // Strip any record-specific id so a new record is created
            // eslint-disable-next-line no-unused-vars
            const { _id, __v, ...body } = captured.data;
            await axiosInstance.put(captured.url, {
              ...body,
              familyMemberId: member.familyMemberId
            });
            saved++;
          } catch {
            // continue with remaining members
          }
        }
        setReplaying(false);
        setLastCreated({ type, saved, total });
      } else {
        setLastCreated({ type, saved: 1, total });
      }
    } else {
      // Cancelled — clean up interceptor
      interceptActiveRef.current = false;
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
      capturedPayloadRef.current = null;
      remainingMembersRef.current = [];
      setFormOpen(false);
      setFirstMember(null);
      setUnsavedChanges({ edit: false, status: false });
    }
  }, []);

  const handleCreate = () => {
    const members = memberRecords
      .filter((m) => selectedPids.has(m.pid))
      .map((m) => ({
        pid: m.pid,
        familyMemberId: m.proBandId ?? m.familyMemberId
      }));
    if (members.length === 0) return;

    selectedTypeRef.current = selectedType;
    totalRef.current = members.length;
    remainingMembersRef.current = members.slice(1);
    capturedPayloadRef.current = null;
    interceptActiveRef.current = true;

    // Intercept the first PUT to a clinical-notes endpoint to capture payload
    interceptorIdRef.current = axiosInstance.interceptors.request.use(
      (config) => {
        if (
          interceptActiveRef.current &&
          !capturedPayloadRef.current &&
          config.method === 'put' &&
          config.url?.includes('/clinical-notes/')
        ) {
          capturedPayloadRef.current = {
            url: config.url,
            data:
              typeof config.data === 'string'
                ? JSON.parse(config.data)
                : config.data
          };
        }
        return config;
      }
    );

    setLastCreated(null);
    setFirstMember(members[0]);
    setFormOpen(true);
    setUnsavedChanges({ edit: false, status: false });
  };

  const formProps = firstMember
    ? {
        handleClose,
        familyMemberId: firstMember.familyMemberId,
        familyId,
        isEditing: false,
        model: {},
        staffMembers,
        staffMemberOptions,
        isDisabled: false,
        setUnsavedChanges,
        data: memberRecords.find((m) => m.pid === firstMember.pid)
      }
    : null;

  const renderForm = () => {
    if (!formProps) return null;
    switch (selectedType) {
      case 'Address':
        return <AddressForm {...formProps} />;
      case 'Appointment':
        return <AppointmentForm {...formProps} />;
      case 'Clinical Note':
        return <ClinicalNotesForm {...formProps} />;
      case 'Consent':
        return <ConsentForm {...formProps} />;
      case 'Contact':
        return <ContactForm {...formProps} />;
      case 'Referral':
        return <ReferralForm {...formProps} />;
      case 'Sample':
        return <SampleForm {...formProps} />;
      case 'Test':
        return <TestForm {...formProps} refreshPedigreeLabels={() => {}} />;
      case 'Treatment':
        return <TreatmentForm {...formProps} />;
      case 'Tissue Pathology':
        return <TissuePathologyForm {...formProps} />;
      default:
        return null;
    }
  };

  const selectedCount = selectedPids.size;

  return (
    <>
      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Toggle header */}
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            px: 3,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
            bgcolor: expanded ? 'grey.100' : 'grey.50',
            userSelect: 'none'
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              letterSpacing: 0.5
            }}
          >
            CREATE CLINICAL RECORD FOR FAMILY MEMBERS
          </Typography>
          {expanded ? (
            <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          )}
        </Box>

        <Collapse in={expanded}>
          <Box
            sx={{
              px: 3,
              py: 2,
              bgcolor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider'
            }}
          >
            {/* Success indicator */}
            {lastCreated && (
              <Alert
                icon={<CheckCircleOutlineIcon fontSize="small" />}
                severity="success"
                onClose={() => setLastCreated(null)}
                sx={{ mb: 1.5, py: 0.5, fontSize: 13 }}
              >
                {lastCreated.type} created for {lastCreated.saved} of{' '}
                {lastCreated.total} member
                {lastCreated.total !== 1 ? 's' : ''}
              </Alert>
            )}

            {/* Replay progress */}
            {replaying && (
              <Alert
                icon={<CircularProgress size={14} />}
                severity="info"
                sx={{ mb: 1.5, py: 0.5, fontSize: 13 }}
              >
                Creating records for remaining members…
              </Alert>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3,
                flexWrap: 'wrap'
              }}
            >
              {/* Record type */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.75,
                    letterSpacing: 0.5
                  }}
                >
                  RECORD TYPE
                </Typography>
                <Select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  input={<OutlinedInput />}
                  displayEmpty
                  size="small"
                  sx={{ width: 200, height: 32, fontSize: 13 }}
                  renderValue={(v) =>
                    v ? (
                      v
                    ) : (
                      <span style={{ color: '#999', fontSize: 13 }}>
                        Select type...
                      </span>
                    )
                  }
                >
                  {CLINICAL_NOTE_TYPES.map((t) => (
                    <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Member selector */}
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontWeight: 700,
                    display: 'block',
                    mb: 0.75,
                    letterSpacing: 0.5
                  }}
                >
                  APPLY TO
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5
                  }}
                >
                  {memberRecords.map((m, i) => {
                    const checked = selectedPids.has(m.pid);
                    return (
                      <Chip
                        key={m.pid}
                        label={`UPN ${m.upn ?? i + 1}`}
                        size="small"
                        color={checked ? 'primary' : 'default'}
                        onClick={() => togglePid(m.pid)}
                        sx={{ cursor: 'pointer', height: 24, fontSize: 12 }}
                      />
                    );
                  })}
                </Box>
              </Box>

              {/* Create button */}
              <Box sx={{ ml: 'auto', alignSelf: 'flex-end' }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!selectedType || selectedCount === 0 || replaying}
                  onClick={handleCreate}
                >
                  Create for {selectedCount} member
                  {selectedCount !== 1 ? 's' : ''}
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </Box>
      {/* Single form dialog — saves to first member; interceptor replays for rest */}
      <Dialog
        open={formOpen}
        onClose={() => handleClose({})}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
            bgcolor: 'grey.900',
            color: 'white'
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3
              }}
            >
              Create New {selectedType}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Will be created for {totalRef.current} member
              {totalRef.current !== 1 ? 's' : ''}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => handleClose({})}
            sx={{ color: 'white' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 3, pt: '24px !important' }}>
          {renderForm()}
        </DialogContent>
      </Dialog>
    </>
  );
}
